<!-- Course Timeline Component -->
<nav class="course-timeline h-full flex flex-col bg-base-100" aria-label="Course timeline" data-testid="course-timeline-section">
  <!-- Header -->
  <div class="timeline-header p-4 border-b border-base-300">
    <div class="flex items-center justify-between gap-2 mb-3">
      <h3 class="font-semibold text-lg user-text-clamp-2 min-w-0" [title]="courseName">{{ courseName }}</h3>
      <div class="flex items-center gap-1 shrink-0">
        <!-- KAN-339: bulk collapse/expand, instructor surfaces only (preview/test) -->
        @if (playerMode !== 'learner' && hasUnits) {
          <button type="button" class="btn btn-ghost btn-sm"
            (click)="toggleAllUnits()"
            [title]="allUnitsExpanded ? 'Collapse all units' : 'Expand all units'"
            [attr.aria-label]="allUnitsExpanded ? 'Collapse all units' : 'Expand all units'"
            data-testid="course-timeline-toggle-all-units-btn">
            <app-icon [name]="allUnitsExpanded ? 'unfold_less' : 'unfold_more'" size="sm"></app-icon>
          </button>
        }
        <button class="btn btn-ghost btn-sm lg:hidden" (click)="close()" title="Close Timeline" aria-label="Close timeline" data-testid="course-timeline-close-btn">
          <app-icon name="close" size="sm"></app-icon>
        </button>
      </div>
    </div>

    <!-- Overall Progress -->
    @if (showProgress) {
      <div class="progress-summary" data-testid="course-timeline-progress">
        <div class="flex items-center justify-between text-sm mb-2">
          <span class="text-base-content/70">Course Progress</span>
          <span class="font-medium">{{ progressSummary.completed }}/{{ progressSummary.total }}</span>
        </div>
        <div class="progress progress-primary">
          <div class="progress-bar" [style.width.%]="progressSummary.percentage"></div>
        </div>
        <div class="text-xs text-base-content/60 mt-1">{{ progressSummary.percentage }}% Complete</div>

        <!-- KAN-465: running cumulative points score across attempted quizzes,
             against the course passing threshold. Hidden until a scored quiz
             has been attempted (percent is non-null only then). -->
        @if (runningScore.percent !== null) {
          <div class="running-score mt-2 pt-2 border-t border-base-300 text-xs" data-testid="course-timeline-running-score">
            <div class="flex items-center justify-between gap-2">
              <span class="text-base-content/70">Running score:</span>
              <span class="font-medium" data-testid="course-timeline-running-score-points">{{ runningScore.pointsText }} ({{ runningScore.percentText }})</span>
            </div>
            @if (runningScore.threshold !== null) {
              <div class="mt-0.5 flex items-center justify-between gap-2">
                <span class="text-base-content/60" data-testid="course-timeline-running-score-threshold">Course passing: {{ runningScore.threshold }}%</span>
                @if (runningScore.belowThreshold) {
                  <span class="font-medium text-error" data-testid="course-timeline-running-score-state">Below passing</span>
                } @else {
                  <span class="font-medium text-success" data-testid="course-timeline-running-score-state">On track</span>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Debug Email line — signed-in user + current view, directly below the progress bar.
         Instructor preview/test only (KAN-672); on by default there, toggleable. -->
    @if (debugLineAvailable) {
      @if (showDebugEmail) {
        <div class="mt-2 flex items-start justify-between gap-2 rounded bg-base-200/60 px-2 py-1 text-xs text-base-content/70"
             data-testid="course-timeline-debug-email">
          <span class="min-w-0 break-all">
            <span class="font-semibold">Debug Email:</span> {{ currentUserEmail }}
            <span class="text-base-content/40">·</span>
            <span class="font-semibold">View:</span> {{ debugViewLabel }}
          </span>
          <button type="button"
            class="shrink-0 text-base-content/40 hover:text-base-content"
            (click)="toggleDebugEmail()"
            title="Hide debug line"
            aria-label="Hide debug email line"
            data-testid="course-timeline-debug-email-toggle">
            <app-icon name="close" size="xs"></app-icon>
          </button>
        </div>
      }
      @if (!showDebugEmail) {
        <div class="mt-2">
          <button type="button"
            class="text-xs text-base-content/40 hover:text-base-content"
            (click)="toggleDebugEmail()"
            data-testid="course-timeline-debug-email-show">
            Show debug
          </button>
        </div>
      }
    }

    <!-- Player Mode Indicator (for moderators) -->
    @if (playerMode !== 'learner') {
      <div class="mt-3">
        <span class="badge badge-sm badge-outline" data-testid="course-timeline-mode-badge">
          {{ playerMode === 'moderator_preview' ? 'Preview Mode' : 'Test Mode' }}
        </span>
      </div>
    }
  </div>

  <!-- Timeline Content -->
  <div class="timeline-content flex-1 overflow-y-auto">
    <div class="p-2">
      <!-- Iterate through grouped items -->
      @for (group of getGroupedItems(); track trackByUnitId($index, group); let isLast = $last) {
        <div class="timeline-group">
          <div [class.mb-4]="!isLast">
            <!-- Direct Modules (not in units) -->
            @if (group.type === 'direct') {
              <div class="direct-modules">
                <div class="items-list space-y-1">
                  @for (item of group.items; track trackByItemId($index, item)) {
                    <div
                      class="timeline-item"
                      [class.current]="item.id === currentItemId">
                      <button [attr.data-testid]="'course-timeline-' + item.id + '-item'" class="item-button w-full p-3 rounded-lg text-left transition-all bg-base-200 border-2 border-transparent"
                        [class.bg-primary]="item.id === currentItemId"
                        [class.text-primary-content]="item.id === currentItemId"
                        [class.hover:border-primary]="item.id !== currentItemId && canAccessItem(item)"
                        [class.cursor-not-allowed]="!canAccessItem(item)"
                        [class.opacity-50]="!canAccessItem(item)"
                        [disabled]="!canAccessItem(item)"
                        (click)="selectItem(item)">
                        <div class="flex items-center gap-3">
                          <!-- Status Icon -->
                          <app-icon [name]="getItemIcon(item)"
                            size="sm"
                          [class]="getItemStatusClass(item)"></app-icon>
                          <!-- Content -->
                          <div class="flex-1 min-w-0">
                            <div class="font-medium text-sm user-text-clamp-2" [title]="item.name">{{ item.name }}</div>
                            <div class="text-xs opacity-75 mt-1 flex flex-wrap items-center gap-2 min-w-0">
                              <span class="user-text-clamp-1">{{ getItemTypeText(item) }}</span>
                              <span class="text-base-content/50">•</span>
                              <span>{{ getEstimatedDuration(item) }}</span>
                              @if (getQuestionCountLabel(item); as questionLabel) {
                                <span class="text-base-content/50">•</span>
                                <span>{{ questionLabel }}</span>
                              }
                            </div>
                          </div>
                          <!-- Current indicator -->
                          @if (item.id === currentItemId) {
                            <div class="text-xs font-medium">
                              Now
                            </div>
                          }
                        </div>
                      </button>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- Unit Groups -->
            @if (group.type === 'unit') {
              <div class="unit-group">
                <!-- Unit Header -->
                <div class="unit-header">
                  <button [attr.data-testid]="'course-timeline-' + group.unitId + '-toggle-btn'" class="unit-toggle w-full p-3 rounded-lg text-left transition-all bg-base-200 border-2 border-transparent hover:border-primary"
                    [class.border-primary]="isCurrentItemInUnit(group.unitId!)"
                    (click)="toggleUnit(group.unitId!)"
                    [attr.aria-expanded]="isUnitExpanded(group.unitId!)"
                    [attr.aria-label]="'Toggle ' + group.unitName + ' unit'">
                    <div class="flex items-center gap-3">
                      <!-- Expand/Collapse Icon -->
                      <app-icon [name]="isUnitExpanded(group.unitId!) ? 'expand_less' : 'expand_more'"
                        size="sm"
                      class="text-base-content/60"></app-icon>
                      <!-- Unit Info -->
                      <div class="flex-1 min-w-0">
                        <div class="font-medium text-sm">
                          <!-- Unit name - clickable if unit has intro -->
                          @if (!hasUnitIntro(group.unitId!)) {
                            <span class="user-text-truncate inline-block max-w-full align-middle" [title]="group.unitName">{{ group.unitName }}</span>
                          }
                          @if (hasUnitIntro(group.unitId!)) {
                            <a
                              (click)="navigateToUnitIntro(group.unitId!, $event)"
                              class="text-primary hover:underline cursor-pointer user-text-truncate inline-block max-w-full align-middle"
                              [title]="group.unitName">
                              {{ group.unitName }}
                            </a>
                          }
                        </div>
                        <div class="text-xs text-base-content/60 mt-1 flex flex-wrap items-center gap-2 min-w-0">
                          <span>{{ getPlayableItemsCount(group) }} items</span>
                          @if (group.hasUnitQuiz) {
                            <span class="text-base-content/50">•</span>
                          }
                          @if (group.hasUnitQuiz) {
                            <span>Unit Quiz</span>
                          }
                        </div>
                      </div>
                      <!-- Unit Progress -->
                      <div class="text-right">
                        <div class="text-xs font-medium">
                          {{ getUnitProgress(group.items).completed }}/{{ getUnitProgress(group.items).total }}
                        </div>
                        <div class="w-12 h-1 bg-base-300 rounded-full mt-1">
                          <div class="h-full bg-primary rounded-full transition-all"
                          [style.width.%]="getUnitProgress(group.items).percentage"></div>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
                <!-- Unit Items (when expanded) -->
                @if (isUnitExpanded(group.unitId!)) {
                  <div
                    class="unit-items ml-4 mt-2 space-y-1 border-l-2 border-base-300 pl-4">
                    @for (item of group.items; track trackByItemId($index, item)) {
                      <div
                        class="timeline-item"
                        [class.current]="item.id === currentItemId">
                        <!-- Unit Overview/Intro item - V1: Always show unit intro in sidebar for navigation -->
                        @if (item.type === 'unit_intro') {
                          <button
                            [attr.data-testid]="'course-timeline-' + item.id + '-item'" class="item-button w-full p-2 rounded-lg text-left transition-all bg-base-200 border-2 border-transparent"
                            [class.bg-primary]="item.id === currentItemId"
                            [class.text-primary-content]="item.id === currentItemId"
                            [class.hover:border-primary]="item.id !== currentItemId"
                            (click)="selectItem(item)">
                            <div class="flex items-center gap-3">
                              <app-icon name="info" size="sm" class="text-info"></app-icon>
                              <div class="flex-1 min-w-0">
                                <div class="font-medium text-sm">Unit Overview</div>
                                <div class="text-xs opacity-75">Introduction</div>
                              </div>
                              @if (item.id === currentItemId) {
                                <div class="text-xs font-medium">Now</div>
                              }
                            </div>
                          </button>
                        }
                        <!-- Regular unit items (modules, quizzes) -->
                        @if (item.type !== 'unit_intro') {
                          <button
                            [attr.data-testid]="'course-timeline-' + item.id + '-item'" class="item-button w-full p-2 rounded-lg text-left transition-all bg-base-200 border-2 border-transparent"
                            [class.bg-primary]="item.id === currentItemId"
                            [class.text-primary-content]="item.id === currentItemId"
                            [class.hover:border-primary]="item.id !== currentItemId && canAccessItem(item)"
                            [class.cursor-not-allowed]="!canAccessItem(item)"
                            [class.opacity-50]="!canAccessItem(item)"
                            [disabled]="!canAccessItem(item)"
                            (click)="selectItem(item)">
                            <div class="flex items-center gap-3">
                              <!-- Status Icon -->
                              <app-icon [name]="getItemIcon(item)"
                                size="sm"
                              [class]="getItemStatusClass(item)"></app-icon>
                              <!-- Content -->
                              <div class="flex-1 min-w-0">
                                <!-- For modules with video and quiz, show detailed breakdown -->
                                @if (item.type === 'module' && item.hasVideo && item.hasQuiz) {
                                  <div>
                                    <div class="font-medium text-sm user-text-clamp-2 mb-1" [title]="item.name">
                                      {{ item.name }}
                                    </div>
                                    <div class="text-xs opacity-75 space-y-0.5 min-w-0">
                                      <!-- KAN-244: getVideoInfo/getQuizInfo embed user-supplied
                                           video/quiz titles. Clamp + break-anywhere (via the
                                           shared .user-text-clamp-2 utility) so an unbreakable
                                           underscore token cannot overflow its box and widen
                                           the sidebar (residual horizontal scroll). -->
                                      <div class="user-text-clamp-2" [title]="getVideoInfo(item)">{{ getVideoInfo(item) }}</div>
                                      <div class="user-text-clamp-2" [title]="getQuizInfo(item)">{{ getQuizInfo(item) }}</div>
                                    </div>
                                    @if ((item.requiredWatchPercentage ?? 90) > 0) {
                                      <div class="mt-1.5">
                                        <span class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200" data-testid="course-timeline-required-badge"><span class="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 dark:bg-amber-400"></span>{{item.requiredWatchPercentage ?? 90}}% required</span>
                                      </div>
                                    }
                                  </div>
                                }
                                <!-- For other items, use original format -->
                                @if (!(item.type === 'module' && item.hasVideo && item.hasQuiz)) {
                                  <div>
                                    <div class="font-medium text-sm user-text-clamp-2" [title]="item.name">
                                      {{ item.name }}
                                    </div>
                                    <div class="text-xs opacity-75 mt-1 flex flex-wrap items-center gap-2 min-w-0">
                                      <span class="user-text-clamp-1">{{ getItemTypeText(item) }}</span>
                                      <span class="text-base-content/50">•</span>
                                      <span>{{ getEstimatedDuration(item) }}</span>
                                      @if (getQuestionCountLabel(item); as questionLabel) {
                                        <span class="text-base-content/50">•</span>
                                        <span>{{ questionLabel }}</span>
                                      }
                                    </div>
                                    <!-- KAN-411 round 2 (side-fix): the "N% required" badge is the
                                         VIDEO-watch requirement, so gate it on the item actually
                                         having a video. Without this a quiz-only module or an
                                         end-of-unit quiz showed a spurious "90% required" (the
                                         `?? 90` default) even though it has no video to watch. -->
                                    @if (item.hasVideo && (item.requiredWatchPercentage ?? 90) > 0) {
                                      <div class="mt-1.5">
                                        <span class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200" data-testid="course-timeline-required-badge"><span class="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 dark:bg-amber-400"></span>{{item.requiredWatchPercentage ?? 90}}% required</span>
                                      </div>
                                    }
                                  </div>
                                }
                              </div>
                              <!-- Current indicator -->
                              @if (item.id === currentItemId) {
                                <div class="text-xs font-medium">
                                  Now
                                </div>
                              }
                            </div>
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
          <!-- Divider between units -->
          @if (group.type === 'unit' && !isLast) {
            <div class="divider my-4"></div>
          }
        </div>
      }
    </div>

    <!-- Empty State -->
    @if (navigationItems.length === 0) {
      <div class="empty-state p-6 text-center" data-testid="course-timeline-empty">
        <app-icon name="school" size="lg" class="text-base-content/30 mb-3"></app-icon>
        <p class="text-base-content/60">No course content available</p>
      </div>
    }
  </div>

  <!-- Legend (for moderators or when helpful) -->
  @if (playerMode !== 'learner') {
    <div class="timeline-footer p-3 border-t border-base-300 text-xs">
      <div class="grid grid-cols-2 gap-2 text-base-content/60">
        <div class="flex items-center gap-1">
          <app-icon name="check_circle" size="xs" class="text-success"></app-icon>
          <span>Completed</span>
        </div>
        <div class="flex items-center gap-1">
          <app-icon name="play_circle" size="xs" class="text-primary"></app-icon>
          <span>Current</span>
        </div>
        <div class="flex items-center gap-1">
          <app-icon name="lock" size="xs" class="text-base-content/30"></app-icon>
          <span>Locked</span>
        </div>
        <div class="flex items-center gap-1">
          <app-icon name="article" size="xs" class="text-base-content/70"></app-icon>
          <span>Available</span>
        </div>
      </div>
    </div>
  }
</nav>